import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, RefreshCw, Search, X as XIcon, Copy, Sparkles } from "lucide-react";
import { api, type PipelineErrorView, type PipelineErrorLevel } from "../../lib/api.js";
import { formatServerDate } from "../../lib/timestamps.js";

/**
 * Errors page — the operator's central failure log. Every pipeline operation
 * that throws (enrich, brief, produce, distribute/*, storyboard, voiceover,
 * viral, publish-sanity, asset uploads) emits a row here. Warnings from
 * orchestrators that return non-empty `warnings: string[]` arrays land as
 * `level: warning` rows.
 *
 *   - 3 KPI tiles: unresolved errors + warnings + 24h-noisiest operations
 *   - Filter chips: level, operation prefix, unresolved-only toggle, keyword id
 *   - Table: timestamp, operation, keyword link, message, status, Resolve button
 *   - Expanded view: full stack + context JSON
 *
 * Polls every 30s so a long-running pipeline surfaces failures without the
 * operator needing to refresh.
 */
export function ErrorsPage() {
  const qc = useQueryClient();
  const [levelFilter, setLevelFilter] = useState<PipelineErrorLevel | null>(null);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [opFilter, setOpFilter] = useState("");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const countsQuery = useQuery({
    queryKey: ["errors", "counts"],
    queryFn: () => api.errorCounts(),
    refetchInterval: 30_000,
  });

  const topOpsQuery = useQuery({
    queryKey: ["errors", "top-operations"],
    queryFn: () => api.topErrorOperations(),
    refetchInterval: 60_000,
  });

  const listQuery = useQuery({
    queryKey: ["errors", "list", { levelFilter, unresolvedOnly, opFilter, keywordFilter }],
    queryFn: () => api.listErrors({
      ...(levelFilter ? { level: levelFilter } : {}),
      ...(unresolvedOnly ? { unresolvedOnly: true } : { unresolvedOnly: false }),
      ...(opFilter ? { operationPrefix: opFilter } : {}),
      ...(keywordFilter ? { keywordId: keywordFilter } : {}),
      limit: 200,
    }),
    refetchInterval: 30_000,
  });

  const resolve = useMutation({
    mutationFn: (id: number) => api.resolveError(id, { by: "operator" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["errors"] });
    },
  });

  const counts = countsQuery.data;

  return (
    <div className="space-y-5 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Errors</h1>
        <p className="mt-1 text-sm text-muted">
          Centralised log of every pipeline failure + warning. Updates every 30s. Click a row to expand its stack and context.
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          icon={<AlertCircle className="h-5 w-5" />}
          label="Unresolved errors"
          value={counts?.unresolved.error ?? 0}
          tone="danger"
        />
        <KpiTile
          icon={<AlertTriangle className="h-5 w-5" />}
          label="Unresolved warnings"
          value={counts?.unresolved.warning ?? 0}
          tone="warn"
        />
        <KpiTile
          icon={<CheckCircle2 className="h-5 w-5" />}
          label="Resolved (all-time)"
          value={(counts?.resolved.error ?? 0) + (counts?.resolved.warning ?? 0)}
          tone="ok"
        />
      </div>

      {/* Noisiest operations (24h window) */}
      {topOpsQuery.data && topOpsQuery.data.operations.length > 0 && (
        <div className="rounded-md border border-line bg-canvas p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted">
            Noisiest operations · last 24h
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {topOpsQuery.data.operations.map((op) => (
              <button
                key={op.operation}
                onClick={() => setOpFilter(op.operation)}
                className="rounded-md border border-line bg-white px-2 py-1 text-xs hover:border-brand hover:text-brand"
              >
                <span className="font-mono">{op.operation}</span>
                <span className="ml-1 text-danger">×{op.n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={unresolvedOnly}
            onChange={(e) => setUnresolvedOnly(e.target.checked)}
          />
          Unresolved only
        </label>
        <select
          value={levelFilter ?? ""}
          onChange={(e) => setLevelFilter((e.target.value as PipelineErrorLevel) || null)}
          className="rounded-md border border-line bg-white px-2 py-1 text-sm"
        >
          <option value="">All levels</option>
          <option value="error">Errors only</option>
          <option value="warning">Warnings only</option>
          <option value="info">Info only</option>
        </select>
        <div className="flex items-center gap-1 rounded-md border border-line bg-white px-2">
          <Search className="h-4 w-4 text-muted" />
          <input
            value={opFilter}
            onChange={(e) => setOpFilter(e.target.value)}
            placeholder="Operation prefix (e.g. distribute., pipeline.viral)"
            className="w-72 bg-transparent py-1 text-sm focus:outline-none"
          />
          {opFilter && (
            <button onClick={() => setOpFilter("")} className="text-muted hover:text-ink">
              <XIcon className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-md border border-line bg-white px-2">
          <Search className="h-4 w-4 text-muted" />
          <input
            value={keywordFilter}
            onChange={(e) => setKeywordFilter(e.target.value)}
            placeholder="Keyword id"
            className="w-48 bg-transparent py-1 text-sm focus:outline-none"
          />
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ["errors"] })}
          className="btn-ghost flex items-center gap-1 text-sm"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Table */}
      {listQuery.isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : listQuery.error ? (
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {(listQuery.error as Error).message}
        </div>
      ) : listQuery.data && listQuery.data.errors.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm">
            <thead className="bg-canvas text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Level</th>
                <th className="px-3 py-2">Operation</th>
                <th className="px-3 py-2">Keyword</th>
                <th className="px-3 py-2">Message</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {listQuery.data.errors.map((e) => (
                <ErrorRow
                  key={e.id}
                  row={e}
                  expanded={expandedId === e.id}
                  onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                  onResolve={() => resolve.mutate(e.id)}
                  resolveInFlight={resolve.isPending && resolve.variables === e.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-md border border-line bg-canvas p-6 text-sm text-muted">
          No errors match the current filters. {unresolvedOnly ? "Try toggling 'Unresolved only' off to see history." : "Nothing's been logged yet."}
        </div>
      )}
    </div>
  );
}

function KpiTile({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: number; tone: "danger" | "warn" | "ok" }) {
  const toneClass =
    tone === "danger" ? "bg-danger/10 text-danger" :
    tone === "warn" ? "bg-warn/10 text-warn" :
    "bg-accent/10 text-accent";
  return (
    <div className="rounded-md border border-line bg-white p-4">
      <div className="flex items-center justify-between">
        <span className={`flex items-center gap-2 rounded px-2 py-1 text-xs font-medium ${toneClass}`}>
          {icon}
          {label}
        </span>
      </div>
      <div className="mt-3 text-3xl font-semibold">{value}</div>
    </div>
  );
}

function ErrorRow({
  row,
  expanded,
  onToggle,
  onResolve,
  resolveInFlight,
}: {
  row: PipelineErrorView;
  expanded: boolean;
  onToggle: () => void;
  onResolve: () => void;
  resolveInFlight: boolean;
}) {
  const ts = formatServerDate(row.occurredAt);
  const levelClass =
    row.level === "error" ? "pill-danger" :
    row.level === "warning" ? "pill-warn" :
    "pill-muted";
  return (
    <>
      <tr className="cursor-pointer border-t border-line/60 hover:bg-canvas" onClick={onToggle}>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-muted">{ts}</td>
        <td className="px-3 py-2">
          <span className={`pill ${levelClass}`}>{row.level}</span>
        </td>
        <td className="px-3 py-2 font-mono text-xs">{row.operation}</td>
        <td className="px-3 py-2 text-xs">
          {row.keywordId ? (
            <a
              href={`#/pipeline?keyword=${encodeURIComponent(row.keywordId)}`}
              className="text-brand hover:underline"
              onClick={(ev) => ev.stopPropagation()}
            >
              {row.keywordId}
            </a>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="max-w-[28rem] truncate px-3 py-2">{row.message}</td>
        <td className="px-3 py-2 text-xs">
          {row.resolved ? <span className="pill pill-ok">resolved</span> : <span className="pill pill-muted">open</span>}
        </td>
        <td className="px-3 py-2 text-right">
          {!row.resolved && (
            <button
              onClick={(ev) => { ev.stopPropagation(); onResolve(); }}
              disabled={resolveInFlight}
              className="btn-ghost text-xs"
            >
              {resolveInFlight ? "…" : "Resolve"}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-canvas">
          <td colSpan={7} className="px-3 py-3" onClick={(ev) => ev.stopPropagation()}>
            <div className="space-y-3 text-xs">
              <div>
                <div className="font-medium text-muted">Full message</div>
                <div className="mt-1 whitespace-pre-wrap rounded bg-white px-3 py-2 font-mono">{row.message}</div>
              </div>
              {row.stack && (
                <div>
                  <div className="font-medium text-muted">Stack</div>
                  <pre className="mt-1 max-h-72 overflow-auto rounded bg-white px-3 py-2 font-mono text-[11px]">{row.stack}</pre>
                </div>
              )}
              {row.context && (
                <div>
                  <div className="font-medium text-muted">Context</div>
                  <pre className="mt-1 max-h-48 overflow-auto rounded bg-white px-3 py-2 font-mono text-[11px]">{JSON.stringify(row.context, null, 2)}</pre>
                </div>
              )}
              {row.suggestedPrompt && <FixPrompt prompt={row.suggestedPrompt} />}
              {row.resolved && (
                <div className="text-muted">
                  Resolved {row.resolvedAt} by {row.resolvedBy ?? "?"}{row.resolvedNote ? ` · ${row.resolvedNote}` : ""}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function FixPrompt({ prompt }: { prompt: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard may be blocked — the textarea below is selectable as a fallback */
    }
  };
  return (
    <div className="rounded-md border border-brand/30 bg-brand-50/50 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-medium text-brand-800">
          <Sparkles className="h-3.5 w-3.5" />
          Fix with Claude
        </div>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 rounded border border-brand/30 bg-white px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
        >
          {copied ? <><CheckCircle2 className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy prompt</>}
        </button>
      </div>
      <p className="mt-1 text-2xs text-muted">Paste this into Claude Code (or the chat panel) to get the root cause + a fix.</p>
      <textarea
        readOnly
        value={prompt}
        onClick={(e) => (e.target as HTMLTextAreaElement).select()}
        className="mt-2 h-32 w-full resize-y rounded border border-line bg-white p-2 font-mono text-[11px] text-muted-dark focus:border-brand focus:outline-none"
      />
    </div>
  );
}
